import { validateId } from '~/repositories/utils';
import { fetchInChunks } from '~/utils/batch';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type {
  CreateLeaseDto,
  HydratedLeaseDto,
  HydratedLeaseDtoPaginationDto,
  HydratedLeasePaginationDto,
  LeaseDto,
  MoveInDto,
  UpdateLeaseDto,
  UpdateLeaseEvictionStatusDto,
} from '~/swagger/portal/Api';
import type { ApiFilter } from '~/types/apiFilters';

const LEASES_URL = '/api/leases';

export interface SuggestedEndDateResponse {
  suggestedEndDate: string;
}
export type LeaseListFilter = ApiFilter<{
  page?: number;
  pageSize?: number;
  unitId?: string;
  monthToMonth?: boolean;
  eviction?: boolean;
  withBalance?: boolean;
  tenantIds?: string[];
  propertyId?: string;
  portfolioId?: string;
  portfolioIds?: string[];
  propertyIds?: string[];
  ended?: boolean;
  draft?: boolean;
  balanceLessThan?: number;
  balanceMoreThan?: number;
  'LeaseStart.Start'?: string;
  'LeaseStart.End'?: string;
  'LeaseEnd.Start'?: string;
  'LeaseEnd.End'?: string;
}>;

export type ExpiringLeaseListFilter = ApiFilter<{
  unitId?: string;
  portfolioIds?: string[];
  propertyIds?: string[];
  unitIds?: string[];
}>;

export type MoveOutsLeaseListFilter = ApiFilter<{
  page?: number;
  pageSize?: number;
  portfolioIds?: string[];
  propertyIds?: string[];
  unitIds?: string[];
  'moveOutDateRange.start'?: string;
  'moveOutDateRange.end'?: string;
}>;

export class LeaseRepository extends BaseRestRepository {
  public async addLease(payload: CreateLeaseDto): Promise<LeaseDto> {
    const url = LEASES_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getLeases(filter?: LeaseListFilter): Promise<HydratedLeasePaginationDto> {
    const url = urlWithQuery(LEASES_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getLease(leaseId: string): Promise<HydratedLeaseDto> {
    const url = `${LEASES_URL}/${leaseId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async bindTenantToLease(leaseId: string, tenantId: string, data: MoveInDto): Promise<boolean> {
    const url = `${LEASES_URL}/${leaseId}/tenants/${tenantId}`;
    const response = await this.fetchWithAuth(url, { method: 'POST', body: JSON.stringify(data) });
    return response.ok;
  }

  public async batchGetLeasesByIds(leaseIds: string[]): Promise<HydratedLeaseDto[]> {
    return fetchInChunks(leaseIds, async (idsChunk) => {
      const url = urlWithQuery(`${LEASES_URL}/batch`, { leaseIds: idsChunk });
      const response = await this.fetchWithAuth(url);
      return this.getJsonResponse(response);
    });
  }

  public async editLease(leaseId: string, payload: UpdateLeaseDto): Promise<LeaseDto> {
    const url = `${LEASES_URL}/${leaseId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteLease(leaseId: string) {
    const url = `${LEASES_URL}/${leaseId}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async eviction(leaseId: string, payload: UpdateLeaseEvictionStatusDto) {
    const url = `${LEASES_URL}/${leaseId}/eviction`;
    await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  public async suggestedLeaseEndDate(startDate: string): Promise<SuggestedEndDateResponse> {
    const url = `${LEASES_URL}/suggested-end-date?startDate=${startDate}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getLeaseOverlapping(unitId: string, startTime: string, endTime?: string): Promise<HydratedLeaseDto[]> {
    const url = urlWithQuery(`${LEASES_URL}/overlapping`, { unitId, startTime, endTime });

    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }
  public async endLease(leaseId: string) {
    const url = `${LEASES_URL}/${leaseId}/end`;
    await this.fetchWithAuth(url, { method: 'POST' });
  }
  public async reactivateLease(leaseId: string) {
    const url = `${LEASES_URL}/${leaseId}/reactivate`;
    await this.fetchWithAuth(url, { method: 'POST' });
  }

  public async sendNotifyBalanceDue(leaseId: string) {
    const url = `${LEASES_URL}/${leaseId}/notify-balance-due`;
    const response = await this.fetchWithAuth(url, { method: 'POST' });
    return this.getJsonResponse(response);
  }

  public async getExpiringLeases(filter?: ExpiringLeaseListFilter): Promise<HydratedLeaseDtoPaginationDto> {
    const url = urlWithQuery(`${LEASES_URL}/expiring`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getProratedAmount(opts: { leaseStartDate?: string; rentAmount: number; firstChargeDate: string }): Promise<{
    proratedAmount: number;
  }> {
    const url = urlWithQuery(`${LEASES_URL}/prorated-amount`, opts);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async activateDraft(leaseId: string): Promise<void> {
    const url = `${LEASES_URL}/${leaseId}/activate-draft`;
    await this.fetchWithAuth(url, { method: 'POST' });
  }

  public async getNextChargeDate(leaseStartDate: string): Promise<{ date: string }> {
    const url = urlWithQuery(`${LEASES_URL}/next-charge-date`, { leaseStartDate });
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getMoveOutsLeasesPaginated(filter?: MoveOutsLeaseListFilter): Promise<any> {
    const url = urlWithQuery(`${LEASES_URL}/move-outs`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async deleteLeaseBill(leaseId: string, billId: string): Promise<void> {
    validateId(leaseId, 'Lease');
    validateId(billId, 'Bill');
    const url = `${LEASES_URL}/${leaseId}/bills/${billId}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }
}

export const leaseRepository = new LeaseRepository();
